import posthog from 'posthog-js'

import config from 'lib/config'
import logError from 'lib/logError'
import { logger } from 'lib/utils/logger'
import { getJourneyCounters, markStage } from 'lib/activationJourney'
import { captureLoginEntry, getEntryTrackingProperties } from 'lib/journeySink'
import {
  clearProfileFlags,
  ensurePersonProfile,
  setEntryIdentity,
  setProfileFlag,
} from 'lib/personProfile'
import { PROFILE_FLAG } from 'lib/personProfile/constants'
import { FUNNEL_STAGE } from 'lib/activationJourney/constants'

import {
  identifyGoogleAnalyticsUser,
  initGoogleAnalytics,
  resetGoogleAnalyticsUser,
  trackGoogleAnalyticsEvent,
} from './googleAnalytics'
import {
  DEMO_EVENT_PREFIX,
  FUNNEL_STAGE_EVENTS,
  POSTHOG_GROUP_SHOPIFY,
  SHOP_PROPERTY,
  STORE_DOMAIN_PROPERTY,
} from './constants'

// Client-side capture ceiling for posthog-js. Raised far above the SDK default
// (10/s, 100 burst) so busy chat pages + full-fidelity session replay never
// drop events in the browser. Real analytics events (trackEvent, $pageview)
// are nowhere near this rate; the ceiling exists to absorb $snapshot bursts on
// heavy-DOM pages (/my-chats, /find) so replays stay complete. Kept high enough
// that real traffic never trips it, while still capping a runaway capture loop.
// Kept as constants (no inline magic numbers).
const POSTHOG_EVENTS_PER_SECOND = 250
const POSTHOG_EVENTS_BURST_LIMIT = 2500

export const initAnalytics = () => {
  // GA4 (gtag.js). Runs alongside PostHog rather than through the GTM
  // container, and no-ops on every env whose GA_MEASUREMENT_ID is blank.
  initGoogleAnalytics()

  return posthog.init(config.POSTHOG_API_KEY, {
    api_host: config.POSTHOG_HOST,
    // api_host routes through our reverse proxy (t.zoko.io); ui_host keeps the
    // toolbar/assets resolving to PostHog itself.
    ui_host: 'https://us.posthog.com',
    // Pins the SDK's dated default behaviors (pageview/rageclick/recording/
    // script-injection/storage). 2026-06-25 is the pinned bundle (this SDK,
    // posthog-js 1.418.x, also ships a newer 2026-08-29 — bump deliberately).
    // Relative to the old 2025-05-24 it turns on:
    // rageclick content/text ignorelists, external scripts injected into <head>,
    // localhost/127.0.0.1 auto-tagged as internal/test users, debounced
    // persistence + split storage, Google-Search-app detection, and session-
    // recording strictMinimumDuration + canvas capture. It ALSO defaults
    // streamNetworkBody (network payload capture) ON — we force that OFF below.
    // Bump this deliberately after reviewing the changelog — never auto-latest.
    defaults: '2026-06-25',
    debug: !config.IS_PROD,
    // Click-only autocapture: every click is recorded except elements carrying
    // the `ph-no-capture` class (the chat send/mic button, which fires on every
    // message). Change/submit events and copied text stay off. Explicit
    // trackEvent() calls remain the source for funnel/business events, and
    // autocapture does not affect $pageview, which is a separate option.
    autocapture: {
      dom_event_allowlist: ['click'],
      capture_copied_text: false,
    },
    // Lift the SDK's client-side limiter off its 10/s, 100-burst default.
    // Live chat pages (/my-chats, /find) churn the DOM enough that
    // full-fidelity session-replay $snapshot volume blew past 10/s and the
    // SDK silently DROPPED events client-side (~1.6k+/week across 15 users,
    // surfaced as the client_ingestion_warning "rate limited" health check).
    rate_limiting: {
      events_per_second: POSTHOG_EVENTS_PER_SECOND,
      events_burst_limit: POSTHOG_EVENTS_BURST_LIMIT,
    },
    // Session replay, full fidelity — no masking (explicit product decision).
    // NOTE: replays capture the whole screen, including live customer PII
    // (chat messages, phone numbers, names, emails). Recording only actually
    // starts if "Record user sessions" is also enabled in the PostHog project
    // settings for each environment.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskTextSelector: '', // record all on-screen text (no text masking)
      // The 2026-06-25 defaults turn streamNetworkBody ON, which would upload
      // network request/response BODIES into replays — message content, contact
      // PII, auth tokens and other customers' data from API payloads, i.e. data
      // that isn't even on screen. Forced off; our session_recording keys
      // override the dated default (defaults merge first, user config wins).
      // canvasCapture + strictMinimumDuration from the default are left intact.
      streamNetworkBody: false,
    },
  })
}

// PostHog strips $groups from any event captured while the visitor is still
// anonymous, so an event that must carry a group cannot simply fire on mount.
// This resolves the moment the visitor is identified, letting such an event
// wait for it instead. Declared before identifyUser, which resolves it.
let resolveIdentified = () => {}
const identified = new Promise((resolve) => {
  resolveIdentified = resolve
})

// Already-identified visitors — a restored session, where identifyUser never
// runs again — resolve immediately rather than waiting on a promise that will
// never settle.
export const whenIdentified = () => {
  if (posthog?._isIdentified?.()) {
    return Promise.resolve()
  }
  return identified
}

// Guard identify against garbage distinct_ids. A falsy id, or the project
// API key itself being passed through, gets rejected server-side as
// `missing_distinct_id` and the event is dropped — so refuse those here
// instead of letting them leave the browser.
export const identifyUser = (distinctId, personProperties) => {
  if (!distinctId || distinctId === config.POSTHOG_API_KEY) {
    return
  }
  posthog.identify(distinctId, personProperties)
  identifyGoogleAnalyticsUser(distinctId)
  resolveIdentified()
}

// Names the Shopify group in the PostHog UI. Previously guarded on _isIdentified(),
// believing $groupidentify is rejected while anonymous. The rejection is about
// person PROCESSING, which ensurePersonProfile() satisfies — so the group is now
// named during onboarding rather than only after login.
export const setShopifyGroup = (shop) => {
  if (!shop) {
    return
  }
  ensurePersonProfile()
  // Group properties are passed so the $groupidentify is emitted even when the
  // key has not changed — registerShopifyGroup will usually have set the key
  // already, and group() only emits on a change.
  posthog.group(POSTHOG_GROUP_SHOPIFY, shop, { name: shop })
}

// Same correction as setShopifyGroup: the server-side drop
// (invalid_event_when_process_person_profile_is_false) is about person processing,
// not identification.
export const setAnalyticsGroup = (groupType, groupKey) => {
  if (!groupKey) {
    return
  }
  ensurePersonProfile()
  posthog.group(groupType, groupKey)
}

export const resetAnalytics = () => {
  resetGoogleAnalyticsUser()
  // Only the de-duplication cache is cleared, never the activation journey:
  // onboarding itself logs the merchant out and back in, so wiping the journey
  // here would lose the signup timestamp mid-funnel.
  clearProfileFlags()
  return posthog.reset()
}

// Registers is_demo_store as a PostHog super-property so it rides on every
// event PostHog sends — manual trackEvent captures plus $pageview and session
// replay metadata. Lets demo engagement be filtered in PostHog without any
// per-event wiring. Unregisters when the active store is not a demo store.
export const setDemoStoreSuperProperty = (isDemoStore) => {
  if (!config.POSTHOG_API_KEY) {
    return
  }
  if (isDemoStore) {
    posthog.register({ is_demo_store: true })
  } else {
    posthog.unregister('is_demo_store')
  }
}

const DEMO_TOAST_TYPE = 'golden'
const DEMO_TOAST_FALLBACK_MESSAGE = 'demo event tracked'

// Debug-mode aid: surfaces a demo event as a toast with the event name in the
// `title` prop, so demo tracking can be watched live in the browser. The
// message carries the most useful payload detail available for context.
const toastDemoEvent = (eventName, payload) => {
  const detail =
    payload?.route ||
    payload?.feature ||
    payload?.card_title ||
    DEMO_TOAST_FALLBACK_MESSAGE
  window?.globals?.service?.toast(String(detail), {
    title: eventName,
    type: DEMO_TOAST_TYPE,
  })
}

// Registers the Shopify group the first time the shop domain is seen, so the
// install funnel can be aggregated on the shop rather than the person.
//
// This deliberately goes through posthog.group() rather than attaching $groups
// to each capture. group() writes $groups into PostHog's OWN persistence
// (localStorage), so every later event carries the association automatically —
// including events in a new tab or a later session, where the journeySink entry
// is gone and `shop` is no longer on the payload. Attaching per event would
// inherit exactly that gap: measured in production, the entry survives on as
// little as 21% of later onboarding events, which would read as merchants
// dropping out of the funnel rather than as a lost identifier.
//
// group() only emits a $groupidentify when the key actually changes, but it
// re-writes $groups into persistence on every call regardless — so the current
// value is checked first rather than paying that write on every event.
const registerShopifyGroup = (payload) => {
  // store_domain is the same value read off the store rather than the entry, so
  // it still resolves in a new tab, a later session or on another device, where
  // the sessionStorage entry is gone. Empty for demo and unbound stores, which
  // the falsy check drops.
  const shop = payload?.[SHOP_PROPERTY] || payload?.[STORE_DOMAIN_PROPERTY]
  if (!shop || posthog?.getGroups?.()?.[POSTHOG_GROUP_SHOPIFY] === shop) {
    return
  }
  posthog.group(POSTHOG_GROUP_SHOPIFY, shop)
}

// Sends a single event to every configured analytics provider. Extracted so the
// same payload can be dispatched twice: once as the real event and once as its
// demo-store mirror (demo_<eventName>) without re-running the allowlist guards.
const dispatchToProviders = (module, eventName, payload) => {
  if (config.POSTHOG_API_KEY) {
    // Every tracked action gets a real person rather than a bare anonymous id. Here
    // and not in initAnalytics() on purpose: a profile per page LOAD would mint one
    // person per crawler and bounce (22.1k anonymous visitors / 126.9k events in 30
    // days); per tracked ACTION covers every human who actually does something. Must
    // precede the capture — the gate is read as the event is built. Guarded because
    // enriching a person must never stop the event being sent.
    try {
      ensurePersonProfile()
    } catch (error) {
      logError(error, 'trackEvent.ensurePersonProfile', { eventName })
    }
    registerShopifyGroup(payload)
    posthog.capture(eventName, {
      ...payload,
      module,
    })
  }

  //GTM dataLayer
  window.dataLayer = window?.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...payload,
    module,
  })

  //GA4
  trackGoogleAnalyticsEvent(eventName, { ...payload, module })
}

// Pushes the activation counters into PostHog as super-properties, so events
// this module never sees — $pageview, autocapture, session replay metadata —
// can still be segmented by funnel stage and demo time. Manual events get the
// exact values merged per capture; these are refreshed on stage change and on
// the demo clock's flush, so they trail by at most one tick.
export const registerJourneySuperProperties = () => {
  if (!config.POSTHOG_API_KEY) {
    return
  }
  posthog.register(getJourneyCounters())
}

const trackEvent = (module, eventName, payload = {}, hideToast = false) => {
  if (config.IS_TEST_MODE) {
    return
  }

  // Advance the funnel clock before the payload is built, so the event that
  // marks a stage already carries that stage and the time the previous one took.
  const stage = FUNNEL_STAGE_EVENTS[eventName]
  if (stage) {
    markStage(stage)
    registerJourneySuperProperties()
    // Reaching the end of the funnel is what "onboarded" means, so the sticky
    // person flag is raised from the same signal that closes the journey.
    if (stage === FUNNEL_STAGE.qr_accepted) {
      setProfileFlag(PROFILE_FLAG.has_onboarded)
    }
  }

  // Login-page params are captured locally before the payload is built, so
  // the visit event itself already reads its own entry back. Guarded: the
  // sink is never allowed to stop the PostHog send.
  try {
    if (eventName === trackEvent.events.auth_login_visit) {
      captureLoginEntry()
      // Carries the door they came through (and the shop, for a Shopify install)
      // from their very first event, rather than staying unnamed until an account
      // exists — which never happens for a merchant who abandons onboarding.
      setEntryIdentity(getEntryTrackingProperties())
    }
  } catch (error) {
    logError(error, 'trackEvent.loginEntry', { eventName })
  }
  // Shopify-entry fields (via_shopify, shop, is_icp, store_url, entry_kind)
  // ride on every event for the rest of the session. Before this only the
  // login visit carried them, so the ICP funnel could not be followed past
  // its first step. Explicit payload and _logData values win on merge.
  payload = {
    ...getJourneyCounters(),
    ...getEntryTrackingProperties(),
    ...window?.globals?._logData,
    ...payload,
  }
  config.DEBUG_MODE && logger('TRACKEVENT:', { module, eventName, payload })
  // if (config.ENV === 'development') return
  const showLogs = !hideToast && config.DEBUG_MODE
  try {
    if (!Object.keys(trackEvent.modules).includes(module)) {
      showLogs &&
        window?.globals?.service?.toast(
          'TRACKEVENT: module not part of trackEvent.modules: ' + eventName,
          { type: 'error' }
        )
      return window.console.error(
        'TRACKEVENT: module not part of trackEvent.modules',
        eventName
      )
    }
    if (!Object.keys(trackEvent.events).includes(eventName)) {
      logger(
        'Object.keys(trackEvent.events)',
        Object.keys(trackEvent.events),
        eventName
      )
      showLogs &&
        window?.globals?.service?.toast(
          'TRACKEVENT: eventName not part of trackEvent.events: ' + eventName,
          { type: 'error' }
        )
      return window.console.error(
        'TRACKEVENT: eventName not part of trackEvent.eventName',
        eventName
      )
    }

    dispatchToProviders(module, eventName, payload)

    // Demo-store mirror: when the active store is a demo store, emit a
    // demo_<eventName> twin of every event so demo engagement can be tracked
    // in isolation. Gated on is_demo_store so the extra volume only applies
    // to demo stores. Skip events already prefixed so we never produce
    // demo_demo_*.
    if (payload?.is_demo_store && !eventName.startsWith(DEMO_EVENT_PREFIX)) {
      const demoEventName = `${DEMO_EVENT_PREFIX}${eventName}`
      dispatchToProviders(module, demoEventName, payload)
      showLogs && toastDemoEvent(demoEventName, payload)
    }

    if (showLogs) {
      // Demo events get the name in the toast `title`; everything else keeps
      // the plain message-only toast.
      if (eventName.startsWith(DEMO_EVENT_PREFIX)) {
        toastDemoEvent(eventName, payload)
      } else {
        window?.globals?.service?.toast(eventName)
      }
    }
  } catch (error) {
    logError(error, 'trackEvent', {
      eventName,
      module,
    })
  }
}

trackEvent.modules = {
  // Onboarding revamp (PRD taxonomy): signup.* funnel/rescue events and the
  // meta_handoff.* install-handoff events are their own namespaces.
  signup: 'signup',
  meta_handoff: 'meta_handoff',
  link_creator: 'link_creator',
  ai: 'ai',
  catalog: 'catalog',
  session_blocker: 'session_blocker',
  whatsapp_calling: 'whatsapp_calling',
  flows: 'flows',
  onboarding: 'onboarding',
  navbar: 'navbar',
  system: 'system',
  announcement: 'announcement',
  broadcast: 'broadcast',
  dock: 'dock',
  auth: 'auth',
  quickfind: 'quickfind',
  billing: 'billing',
  chat: 'chat',
  settings: 'settings',
  ctwa: 'ctwa',
  integrations: 'integrations',
  apps: 'apps',
  templates: 'templates',
  quick_replies: 'quick_replies',
  admin: 'admin',
  hub: 'hub',
  demo: 'demo',
}

/**
 * component_action
 * e.g: conversation_click
 **/
trackEvent.events = {
  // ai
  ai_bot_language_select: 'ai_bot_language_select',
  ai_variable_remove_click: 'ai_variable_remove_click',
  ai_variable_chip_click: 'ai_variable_chip_click',
  ai_variable_drag_drop: 'ai_variable_drag_drop',
  ai_expandable_field_resize: 'ai_expandable_field_resize',
  ai_guru_suggestion_click: 'ai_guru_suggestion_click',
  ai_guru_suggestion_close_click: 'ai_guru_suggestion_close_click',
  ai_guru_suggestion_edit_click: 'ai_guru_suggestion_edit_click',
  ai_guru_suggestion_send_click: 'ai_guru_suggestion_send_click',
  // The ticker leaving the screen, not a user dismissing it — `reason`
  // carries which. Paired with _show so dwell can be joined to the card.
  ai_impact_ticker_dismiss: 'ai_impact_ticker_dismiss',
  // Fires on every Zoko AI page view, card or no card: `status` is the
  // split between merchants who were shown the ticker and those who were not.
  ai_impact_ticker_show: 'ai_impact_ticker_show',
  ai_sello_payment_duplicate_name_found:
    'ai_sello_payment_duplicate_name_found',
  ai_sello_shipping_duplicate_name_found:
    'ai_sello_shipping_duplicate_name_found',

  // templates
  templates_favourite_toggle: 'templates_favourite_toggle',
  templates_create_new_click: 'templates_create_new_click',
  templates_header_drop_upload: 'templates_header_drop_upload',
  templates_header_file_download_click: 'templates_header_file_download_click',
  quick_replies_attachment_drop_upload: 'quick_replies_attachment_drop_upload',

  // expandable field (common/UI/ExpandableField) — the component is shared, so
  // every surface that mounts it registers its own pair under its own module
  chat_expandable_field_resize: 'chat_expandable_field_resize',
  chat_expandable_field_emoji_select: 'chat_expandable_field_emoji_select',
  broadcast_expandable_field_resize: 'broadcast_expandable_field_resize',
  broadcast_expandable_field_emoji_select:
    'broadcast_expandable_field_emoji_select',
  flows_expandable_field_resize: 'flows_expandable_field_resize',
  flows_expandable_field_emoji_select: 'flows_expandable_field_emoji_select',

  // catalog
  catalog_banner_click: 'catalog_banner_click',
  catalog_payment_duplicate_name_found: 'catalog_payment_duplicate_name_found',
  catalog_publish_toggle: 'catalog_publish_toggle',
  catalog_save_click: 'catalog_save_click',
  catalog_save_next_click: 'catalog_save_next_click',

  // session_blocker
  session_blocker_continue_here_click: 'session_blocker_continue_here_click',
  session_blocker_logout_click: 'session_blocker_logout_click',
  session_blocker_show: 'session_blocker_show',

  // whatsapp_calling
  whatsapp_calling_call_ended: 'whatsapp_calling_call_ended',
  whatsapp_calling_card_shown: 'whatsapp_calling_card_shown',
  whatsapp_calling_settings_save: 'whatsapp_calling_settings_save',
  whatsapp_calling_toggle: 'whatsapp_calling_toggle',

  // flows
  flows_canvas_debug_close_click: 'flows_canvas_debug_close_click',
  flows_canvas_debug_filter_apply_click:
    'flows_canvas_debug_filter_apply_click',
  flows_canvas_debug_instance_select_click:
    'flows_canvas_debug_instance_select_click',
  flows_canvas_debug_node_open_click: 'flows_canvas_debug_node_open_click',
  flows_canvas_debug_open_click: 'flows_canvas_debug_open_click',
  flows_canvas_debug_state_filter_click:
    'flows_canvas_debug_state_filter_click',
  flows_canvas_debug_stop_click: 'flows_canvas_debug_stop_click',
  flows_canvas_auto_arrange_click: 'flows_canvas_auto_arrange_click',
  flows_canvas_auto_arrange_undo_click: 'flows_canvas_auto_arrange_undo_click',
  flows_canvas_footer_close_click: 'flows_canvas_footer_close_click',
  flows_canvas_footer_save_click: 'flows_canvas_footer_save_click',
  flows_variable_remove_click: 'flows_variable_remove_click',
  flows_variable_chip_click: 'flows_variable_chip_click',
  flows_variable_drag_drop: 'flows_variable_drag_drop',
  flows_canvas_message_file_download_click:
    'flows_canvas_message_file_download_click',
  flows_canvas_free_to_paid_plan_nudge: 'flows_canvas_free_to_paid_plan_nudge',
  flows_canvas_header_back_click: 'flows_canvas_header_back_click',
  flows_canvas_header_close_click: 'flows_canvas_header_close_click',
  flows_canvas_header_publish_click: 'flows_canvas_header_publish_click',
  flows_canvas_header_use_playbook_click:
    'flows_canvas_header_use_playbook_click',
  flows_canvas_publish_no_credits: 'flows_canvas_publish_no_credits',
  flows_library_recommended_modal_close:
    'flows_library_recommended_modal_close',
  flows_library_recommended_modal_open: 'flows_library_recommended_modal_open',
  flows_library_recommended_modal_publish_click:
    'flows_library_recommended_modal_publish_click',

  // onboarding
  onboarding_apply_default_templates: 'onboarding_apply_default_templates',
  onboarding_not_eligible_logout_click: 'onboarding_not_eligible_logout_click',
  // The merchant reached the /meta-connect screen. Kept under this name — not
  // renamed to match the screen that fires it — because the activation funnel,
  // the journey sink and the admin journeys dashboard all key on it, and
  // markStage is forward-only, so a rename would silently break the stage.
  // The rest of the onboarding_connect_account_* family went with the card
  // that fired them; the screen's own CTAs report as onboarding_mqo_*.
  onboarding_connect_account_visit: 'onboarding_connect_account_visit',
  onboarding_connect_success: 'onboarding_connect_success',
  onboarding_connect_whatsapp: 'onboarding_connect_whatsapp',
  onboarding_create_account_click: 'onboarding_create_account_click',
  onboarding_demo_store_create_error: 'onboarding_demo_store_create_error',
  onboarding_demo_store_create_success: 'onboarding_demo_store_create_success',
  onboarding_meta_connect_success: 'onboarding_meta_connect_success',
  onboarding_meta_connect_visit: 'onboarding_meta_connect_visit',
  onboarding_mqo_business_phone_duplicate_error:
    'onboarding_mqo_business_phone_duplicate_error',
  onboarding_mqo_business_phone_duplicate_error_close_click:
    'onboarding_mqo_business_phone_duplicate_error_close_click',
  onboarding_mqo_business_phone_duplicate_error_contact_us_click:
    'onboarding_mqo_business_phone_duplicate_error_contact_us_click',
  onboarding_mqo_business_phone_duplicate_error_go_back_to_login_click:
    'onboarding_mqo_business_phone_duplicate_error_go_back_to_login_click',
  // "Next" under the business number. It marks the number verified locally
  // rather than running an OTP round trip, so this click is the only record
  // that the merchant cleared step one.
  onboarding_mqo_business_phone_next_click:
    'onboarding_mqo_business_phone_next_click',
  onboarding_mqo_contact_phone_input: 'onboarding_mqo_contact_phone_input',
  onboarding_mqo_delete_whatsapp_account_click:
    'onboarding_mqo_delete_whatsapp_account_click',
  onboarding_mqo_disable_2fa_for_whatsapp_api_click:
    'onboarding_mqo_disable_2fa_for_whatsapp_api_click',
  onboarding_mqo_fb_object_setup: 'onboarding_mqo_fb_object_setup',
  onboarding_mqo_free_number_request: 'onboarding_mqo_free_number_request',
  onboarding_mqo_lead: 'onboarding_mqo_lead',
  onboarding_mqo_meta_connect_cloud_error:
    'onboarding_mqo_meta_connect_cloud_error',
  onboarding_checklist_meta_billing_verify:
    'onboarding_checklist_meta_billing_verify',
  onboarding_mqo_meta_connect_cloud_request:
    'onboarding_mqo_meta_connect_cloud_request',
  onboarding_mqo_meta_connect_cloud_success:
    'onboarding_mqo_meta_connect_cloud_success',
  onboarding_mqo_meta_connect_error: 'onboarding_mqo_meta_connect_error',
  onboarding_mqo_meta_connect_display_name_submit:
    'onboarding_mqo_meta_connect_display_name_submit',
  onboarding_mqo_meta_connect_refocus: 'onboarding_mqo_meta_connect_refocus',
  onboarding_mqo_meta_connect_request: 'onboarding_mqo_meta_connect_request',
  onboarding_mqo_meta_connect_success: 'onboarding_mqo_meta_connect_success',
  onboarding_mqo_meta_connect_support_widget_click:
    'onboarding_mqo_meta_connect_support_widget_click',
  onboarding_mqo_meta_payment_status_modal_load:
    'onboarding_mqo_meta_payment_status_modal_load',
  onboarding_mqo_migration_select: 'onboarding_mqo_migration_select',
  onboarding_mqo_migration_visit: 'onboarding_mqo_migration_visit',
  onboarding_mqo_otp_resend_error: 'onboarding_mqo_otp_resend_error',
  onboarding_mqo_otp_resend_request: 'onboarding_mqo_otp_resend_request',
  onboarding_mqo_otp_resend_success: 'onboarding_mqo_otp_resend_success',
  onboarding_mqo_otp_verification_error:
    'onboarding_mqo_otp_verification_error',
  onboarding_mqo_otp_verification_request:
    'onboarding_mqo_otp_verification_request',
  onboarding_mqo_otp_verification_success:
    'onboarding_mqo_otp_verification_success',
  onboarding_mqo_request_verification_error:
    'onboarding_mqo_request_verification_error',
  onboarding_mqo_request_verification_request:
    'onboarding_mqo_request_verification_request',
  onboarding_mqo_request_verification_success:
    'onboarding_mqo_request_verification_success',
  onboarding_mqo_save_lead_error: 'onboarding_mqo_save_lead_error',
  onboarding_mqo_session_log_error: 'onboarding_mqo_session_log_error',
  onboarding_mqo_session_log_step: 'onboarding_mqo_session_log_step',
  onboarding_mqo_session_logs: 'onboarding_mqo_session_logs',
  onboarding_mqo_website_field_load: 'onboarding_mqo_website_field_load',
  onboarding_mqo_website_verification_request:
    'onboarding_mqo_website_verification_request',
  onboarding_mqo_whatsapp_display_name_guidelines_click:
    'onboarding_mqo_whatsapp_display_name_guidelines_click',
  onboarding_mqo_whatsapp_display_name_validation_request:
    'onboarding_mqo_whatsapp_display_name_validation_request',
  onboarding_qr_code_accepted: 'onboarding_qr_code_accepted',
  onboarding_qr_shown: 'onboarding_qr_shown',
  onboarding_qr_unable_to_scan_click: 'onboarding_qr_unable_to_scan_click',
  onboarding_qr_back_to_qr_click: 'onboarding_qr_back_to_qr_click',
  onboarding_account_issue_refresh_click:
    'onboarding_account_issue_refresh_click',
  onboarding_otp_callback_requested: 'onboarding_otp_callback_requested',
  onboarding_shopify_contact_phone_input:
    'onboarding_shopify_contact_phone_input',
  onboarding_shopify_welcome_contact_no_back_click:
    'onboarding_shopify_welcome_contact_no_back_click',
  onboarding_shopify_welcome_contact_submit_back_click:
    'onboarding_shopify_welcome_contact_submit_back_click',
  onboarding_shopify_welcome_login_click:
    'onboarding_shopify_welcome_login_click',
  onboarding_shopify_welcome_continue_click:
    'onboarding_shopify_welcome_continue_click',
  onboarding_shopify_welcome_prompt_show:
    'onboarding_shopify_welcome_prompt_show',
  onboarding_shopify_welcome_prompt_widget_click:
    'onboarding_shopify_welcome_prompt_widget_click',
  onboarding_icp_override_used: 'onboarding_icp_override_used',
  // ZC-3288: a non-ICP install submitted the lead form (Next).
  onboarding_non_icp_lead_submit: 'onboarding_non_icp_lead_submit',
  // Diagnostic for the install funnel's "shop connected" step: the bind call
  // failed, so the shop never got attached to the Zoko store. Never a funnel
  // step itself — it fires on failure, so as a step it would advance only the
  // merchants who failed.
  onboarding_shopify_store_connect_error:
    'onboarding_shopify_store_connect_error',
  // /store-matcher sent the bind call — `source` auto_connect (the countdown
  // ran out) or connect_click. Pairs with _connect_error; the success is the
  // backend's store_match_success.
  onboarding_shopify_store_connect_start:
    'onboarding_shopify_store_connect_start',
  // /store-matcher skipped the bind call. `reason` already_connected: the shop
  // is bound from an earlier visit — with `source` store_matcher_entry the
  // merchant is sent into the app, which is not a failure (for the install
  // funnel it is an exclusion at "shop connected"). `reason` no_access: a
  // disabled store row was clicked. `reason` unverified: the same, for a store
  // whose role and bound shop the access lookup could not read.
  onboarding_shopify_store_connect_blocked:
    'onboarding_shopify_store_connect_blocked',
  // /store-matcher's zero-candidate CTA asked for a fresh store to bind the
  // shop to. Pairs with _create_error; success is implied by the
  // _connect_start (source create_new_store) that follows it.
  onboarding_shopify_store_create_start:
    'onboarding_shopify_store_create_start',
  onboarding_shopify_store_create_error:
    'onboarding_shopify_store_create_error',
  onboarding_shopify_store_select: 'onboarding_shopify_store_select',
  onboarding_shopify_welcome_show: 'onboarding_shopify_welcome_show',
  onboarding_shopify_welcome_unable_to_scan_click:
    'onboarding_shopify_welcome_unable_to_scan_click',
  onboarding_shopify_welcome_unable_to_scan_submitted:
    'onboarding_shopify_welcome_unable_to_scan_submitted',
  onboarding_signup_click: 'onboarding_signup_click',
  onboarding_whatsapp_blocker_click: 'onboarding_whatsapp_blocker_click',
  onboarding_whatsapp_connect_api_call: 'onboarding_whatsapp_connect_api_call',
  onboarding_whatsapp_connect_callback_received:
    'onboarding_whatsapp_connect_callback_received',
  onboarding_whatsapp_connect_success_page_call:
    'onboarding_whatsapp_connect_success_page_call',

  // navbar
  navbar_account_settings_click: 'navbar_account_settings_click',
  navbar_add_inbox_click: 'navbar_add_inbox_click',
  navbar_add_inbox_folder_click: 'navbar_add_inbox_folder_click',
  navbar_agent_analytics_click: 'navbar_agent_analytics_click',
  navbar_ai_automation_click: 'navbar_ai_automation_click',
  navbar_analytics_click: 'navbar_analytics_click',
  navbar_change_agent_click: 'navbar_change_agent_click',
  navbar_create_new_org_click: 'navbar_create_new_org_click',
  navbar_send_broadcast_click: 'navbar_send_broadcast_click',
  navbar_store_switch: 'navbar_store_switch',

  // system
  system_app_crash: 'system_app_crash',
  system_conversation_subscription_missing:
    'system_conversation_subscription_missing',
  system_conversations_same_timestamp: 'system_conversations_same_timestamp',
  system_file_upload_failed: 'system_file_upload_failed',
  system_health_banner_show: 'system_health_banner_show',
  system_internet_offline_toast: 'system_internet_offline_toast',
  // Onboarding tracker mute toggle — `surface` says which board it was hit
  // from (admin / admin_full / debug), `muted` the state it moved to.
  system_onboarding_sound_muted: 'system_onboarding_sound_muted',
  // Onboarding tracker volume slider — once per drag, `volume` (0..1) where it
  // settled and `surface` which board it was dragged on.
  system_onboarding_sound_volume: 'system_onboarding_sound_volume',
  system_pinned_chats_overflow: 'system_pinned_chats_overflow',
  system_realtime_invalid_topic_subscription:
    'system_realtime_invalid_topic_subscription',
  system_realtime_subscription_missed: 'system_realtime_subscription_missed',
  system_reload_lazy_click: 'system_reload_lazy_click',
  // ZC-3273: a link rendered inside a toast message (ToastPresent `link`),
  // `id` says which toast, `href` where it went.
  system_toast_link_click: 'system_toast_link_click',

  // announcement
  announcement_accept: 'announcement_accept',
  announcement_contact_support: 'announcement_contact_support',
  announcement_dismiss: 'announcement_dismiss',
  announcement_learn_more: 'announcement_learn_more',
  announcement_rolling_popup_close: 'announcement_rolling_popup_close',
  announcement_rolling_popup_cta: 'announcement_rolling_popup_cta',
  announcement_rolling_popup_show: 'announcement_rolling_popup_show',
  announcement_show: 'announcement_show',

  // broadcast
  broadcast_create_add_contacts_click: 'broadcast_create_add_contacts_click',
  broadcast_fill_variables_drop_upload: 'broadcast_fill_variables_drop_upload',
  broadcast_create_advanced_import_toggle:
    'broadcast_create_advanced_import_toggle',
  broadcast_create_back_click: 'broadcast_create_back_click',
  broadcast_create_breadcrumb_click: 'broadcast_create_breadcrumb_click',
  broadcast_create_cancel_click: 'broadcast_create_cancel_click',
  broadcast_create_conversion_tracking_toggle:
    'broadcast_create_conversion_tracking_toggle',
  broadcast_create_next_click: 'broadcast_create_next_click',
  broadcast_create_reattempts_toggle: 'broadcast_create_reattempts_toggle',
  broadcast_create_save_as_draft_click: 'broadcast_create_save_as_draft_click',
  broadcast_create_schedule_select: 'broadcast_create_schedule_select',
  broadcast_create_send_failed: 'broadcast_create_send_failed',
  broadcast_create_send_success: 'broadcast_create_send_success',
  broadcast_create_sheet_add_row: 'broadcast_create_sheet_add_row',
  broadcast_create_sheet_go_to_row: 'broadcast_create_sheet_go_to_row',
  broadcast_create_template_select: 'broadcast_create_template_select',
  broadcast_create_type_select: 'broadcast_create_type_select',

  // dock
  dock_bottom_expand_click: 'dock_bottom_expand_click',
  dock_send_number_request_click: 'dock_send_number_request_click',
  dock_bsuid_copy: 'dock_bsuid_copy',
  dock_bsuid_learn_more_click: 'dock_bsuid_learn_more_click',
  dock_bsuid_settings_link_click: 'dock_bsuid_settings_link_click',
  dock_customer_profile_edit_name: 'dock_customer_profile_edit_name',
  dock_customer_profile_edit_name_cancel:
    'dock_customer_profile_edit_name_cancel',
  dock_customer_profile_edit_name_save: 'dock_customer_profile_edit_name_save',
  dock_customer_profile_media_click: 'dock_customer_profile_media_click',
  dock_customer_profile_media_tabs_click:
    'dock_customer_profile_media_tabs_click',
  dock_customer_profile_sections_click: 'dock_customer_profile_sections_click',
  dock_orders_action_click: 'dock_orders_action_click',
  dock_orders_card_toggle: 'dock_orders_card_toggle',
  dock_orders_search: 'dock_orders_search',
  dock_orders_tab_change: 'dock_orders_tab_change',
  dock_tab_click: 'dock_tab_click',
  dock_tags_delete_tag: 'dock_tags_delete_tag',

  // auth
  auth_2fa_setup_success: 'auth_2fa_setup_success',
  auth_passcode_generate: 'auth_passcode_generate',
  auth_passcode_login: 'auth_passcode_login',
  auth_autologin_success: 'auth_autologin_success',
  // ZC-3302: the QR tab signed itself back in after 2FA activation revoked
  // its session. A drop between activation and success means merchants are
  // still being bounced to the login form after scanning.
  auth_mfa_reauth_success: 'auth_mfa_reauth_success',
  auth_mfa_reauth_failed: 'auth_mfa_reauth_failed',
  auth_create_account_visit: 'auth_create_account_visit',
  auth_signup_entry_redeem_success: 'auth_signup_entry_redeem_success',
  auth_signup_entry_redeem_error: 'auth_signup_entry_redeem_error',
  auth_signup_entry_manual_login: 'auth_signup_entry_manual_login',
  // A Shopify install that landed in a session that already existed, so the
  // redeem never ran and auth_signup_entry_redeem_success never fires. Exists
  // purely so the install funnel can EXCLUDE these merchants rather than count
  // them as a drop at the "Zoko account exists" step — the funnel is for shops
  // new to Zoko, and kind=auto_login only removes the signed-out half.
  auth_shopify_entry_existing_session: 'auth_shopify_entry_existing_session',
  auth_invite_accept_error: 'auth_invite_accept_error',
  auth_invite_link_open: 'auth_invite_link_open',
  auth_invite_mfa_enrol_done: 'auth_invite_mfa_enrol_done',
  auth_invite_password_set_error: 'auth_invite_password_set_error',
  auth_invite_resolve_error: 'auth_invite_resolve_error',
  auth_invite_signin_success: 'auth_invite_signin_success',
  auth_invite_signup_blocked: 'auth_invite_signup_blocked',
  auth_log_out: 'auth_log_out',
  // ZC-3273: the No-account-found screen the login-only gate shows in place
  // of a toast. Its Shopify link reuses auth_login_signup_link_click with a
  // `source` of no_account_found (chat_no_store_access from the chat list's
  // no-store card).
  auth_login_blocked_contact_support_click:
    'auth_login_blocked_contact_support_click',
  auth_login_blocked_go_back_click: 'auth_login_blocked_go_back_click',
  // `/login?create=1` used an email+password to create an account for an
  // address nobody owned — the one exception to the login-only gate.
  // `via` says which override allowed it.
  auth_login_account_create: 'auth_login_account_create',
  auth_login_flow: 'auth_login_flow',
  auth_login_mode_switch: 'auth_login_mode_switch',
  auth_login_security: 'auth_login_security',
  auth_login_signup_blocked: 'auth_login_signup_blocked',
  // ZC-3268: the "Install our Shopify app" link under the sign-in card.
  auth_login_signup_link_click: 'auth_login_signup_link_click',
  auth_login_visit: 'auth_login_visit',
  auth_mfa_enroll_error: 'auth_mfa_enroll_error',
  auth_mfa_enroll_request_otp: 'auth_mfa_enroll_request_otp',
  auth_mfa_login_error: 'auth_mfa_login_error',
  auth_mfa_login_request_otp: 'auth_mfa_login_request_otp',
  auth_recaptcha_skipped: 'auth_recaptcha_skipped',
  auth_recaptcha_verified: 'auth_recaptcha_verified',
  auth_recaptcha_blocked: 'auth_recaptcha_blocked',
  auth_recaptcha_error: 'auth_recaptcha_error',
  auth_signup_flow: 'auth_signup_flow',
  auth_signup_lead: 'auth_signup_lead',
  auth_store_switch_no_access: 'auth_store_switch_no_access',
  auth_track_reditus: 'auth_track_reditus',
  auth_verify_phone_submit: 'auth_verify_phone_submit',

  // quickfind
  quickfind_clear_click: 'quickfind_clear_click',
  quickfind_flows_filter_tab_click: 'quickfind_flows_filter_tab_click',
  quickfind_hide_closed_chats: 'quickfind_hide_closed_chats',
  quickfind_inbox_created: 'quickfind_inbox_created',

  // billing
  billing_facebook_setup_click: 'billing_facebook_setup_click',
  billing_plan_compare_click: 'billing_plan_compare_click',
  billing_plan_pricing_click: 'billing_plan_pricing_click',
  billing_plan_select: 'billing_plan_select',
  billing_phone_pe_checkout_cancelled: 'billing_phone_pe_checkout_cancelled',
  billing_phone_pe_checkout_concluded: 'billing_phone_pe_checkout_concluded',
  billing_phone_pe_checkout_initiated: 'billing_phone_pe_checkout_initiated',
  billing_shopify_activate_click: 'billing_shopify_activate_click',
  billing_shopify_currency_mismatch: 'billing_shopify_currency_mismatch',
  billing_shopify_migration_agreement_click:
    'billing_shopify_migration_agreement_click',
  billing_shopify_migration_credits_migrate_failure:
    'billing_shopify_migration_credits_migrate_failure',
  billing_shopify_migration_credits_migrate_success:
    'billing_shopify_migration_credits_migrate_success',
  billing_shopify_migration_reset_failure:
    'billing_shopify_migration_reset_failure',
  billing_shopify_migration_reset_success:
    'billing_shopify_migration_reset_success',
  billing_shopify_migration_warning_modal_shown:
    'billing_shopify_migration_warning_modal_shown',
  billing_stripe_checkout_initiated: 'billing_stripe_checkout_initiated',
  billing_stripe_success_page_reached: 'billing_stripe_success_page_reached',
  billing_update_subscription_plan: 'billing_update_subscription_plan',
  billing_upgrade_click: 'billing_upgrade_click',

  // chat
  chat_conversation_list_retry_click: 'chat_conversation_list_retry_click',
  chat_history_cursor_at_risk: 'chat_history_cursor_at_risk',
  chat_instagram_reel_link_click: 'chat_instagram_reel_link_click',
  chat_instagram_reel_preview_click: 'chat_instagram_reel_preview_click',
  chat_instagram_reply_via_dm: 'chat_instagram_reply_via_dm',
  chat_media_sent_unconverted: 'chat_media_sent_unconverted',
  chat_messages_retry_click: 'chat_messages_retry_click',
  chat_mobile_app_banner_accept: 'chat_mobile_app_banner_accept',
  chat_mobile_app_banner_dismiss: 'chat_mobile_app_banner_dismiss',
  chat_mobile_app_banner_show: 'chat_mobile_app_banner_show',
  chat_send_message_failure: 'chat_send_message_failure',
  chat_window_conversation_list_refresh_click:
    'chat_window_conversation_list_refresh_click',
  chat_window_enigma_takeover_click: 'chat_window_enigma_takeover_click',
  chat_window_file_upload_attempt: 'chat_window_file_upload_attempt',
  chat_window_file_upload_success: 'chat_window_file_upload_success',
  chat_window_message_delivery_failed: 'chat_window_message_delivery_failed',
  chat_window_message_send_failed: 'chat_window_message_send_failed',
  chat_window_parent_message_click: 'chat_window_parent_message_click',
  chat_window_rtl_toggle_click: 'chat_window_rtl_toggle_click',
  chat_window_support_template_send: 'chat_window_support_template_send',
  chat_window_support_template_change: 'chat_window_support_template_change',
  chat_window_support_window_what_is_this_click:
    'chat_window_support_window_what_is_this_click',
  // Fallback path: no support template could be loaded, so the agent used the
  // old "Click to send Template message" button instead.
  chat_window_expired_template_fallback_click:
    'chat_window_expired_template_fallback_click',

  // settings
  settings_agent_self_delete_blocked: 'settings_agent_self_delete_blocked',
  settings_contacts_export: 'settings_contacts_export',
  settings_tyntec_migration_banner_show:
    'settings_tyntec_migration_banner_show',
  settings_tyntec_migration_cta_click: 'settings_tyntec_migration_cta_click',
  settings_bsuid_mode_change: 'settings_bsuid_mode_change',
  settings_bsuid_change_message_click: 'settings_bsuid_change_message_click',
  settings_bsuid_template_selected: 'settings_bsuid_template_selected',
  settings_bsuid_template_duplicate_click:
    'settings_bsuid_template_duplicate_click',
  settings_bsuid_save: 'settings_bsuid_save',

  // ctwa
  ctwa_list_open: 'ctwa_list_open',
  ctwa_overview_data_tab_open: 'ctwa_overview_data_tab_open',
  ctwa_overview_export_click: 'ctwa_overview_export_click',
  ctwa_overview_open: 'ctwa_overview_open',

  // integrations
  integrations_card_connect_click: 'integrations_card_connect_click',
  integrations_card_delete_click: 'integrations_card_delete_click',
  integrations_modal_connect_click: 'integrations_modal_connect_click',
  integrations_shopify_merger: 'integrations_shopify_merger',
  integrations_shopify_merger_mismatch: 'integrations_shopify_merger_mismatch',
  integrations_whatsapp_pay_cancel: 'integrations_whatsapp_pay_cancel',
  integrations_whatsapp_pay_connect: 'integrations_whatsapp_pay_connect',

  // apps
  apps_card_click: 'apps_card_click',
  apps_shopify_app_embed_open_app_embeds_click:
    'apps_shopify_app_embed_open_app_embeds_click',
  apps_shopify_app_embed_video_click: 'apps_shopify_app_embed_video_click',

  // admin
  admin_disable_bulk_2fa: 'admin_disable_bulk_2fa',

  // hub
  hub_date_range_change: 'hub_date_range_change',
  hub_revenue_table_tab_change: 'hub_revenue_table_tab_change',
  hub_sidebar_click: 'hub_sidebar_click',
  hub_suggestion_click: 'hub_suggestion_click',

  // demo — only fired when the current store is a demo store (is_demo === true),
  // to keep event volume limited to demo engagement tracking.
  demo_add_store_click: 'demo_add_store_click',
  demo_banner_connect_click: 'demo_banner_connect_click',
  demo_banner_show: 'demo_banner_show',
  demo_conversation_open: 'demo_conversation_open',
  demo_feature_close: 'demo_feature_close',
  demo_feature_open: 'demo_feature_open',
  demo_landing_view: 'demo_landing_view',
  demo_message_send: 'demo_message_send',
  demo_quick_reply_click: 'demo_quick_reply_click',
  demo_session_end: 'demo_session_end',
  demo_time_milestone: 'demo_time_milestone',

  // signup (connection-pipeline: ZC-3098/3099/3100)
  meta_handoff_result: 'meta_handoff_result',
  signup_callback_offer_click: 'signup_callback_offer_click',
  signup_callback_offer_close_click: 'signup_callback_offer_close_click',
  signup_callback_try_otp_again_click: 'signup_callback_try_otp_again_click',
  signup_connection_check_retry_click: 'signup_connection_check_retry_click',
  signup_delete_demo_continue_click: 'signup_delete_demo_continue_click',
  signup_delete_demo_different_number_click:
    'signup_delete_demo_different_number_click',
  signup_getting_ready_retry_clicked: 'signup_getting_ready_retry_clicked',
  signup_logout: 'signup_logout',
  signup_number_guide_click: 'signup_number_guide_click',
  // Every onboarding API call (store/signupApiTracking): `api` names the
  // endpoint (SIGNUP_APIS), plus status_code, duration_ms and, on failure,
  // reason.
  signup_api_failed: 'signup_api_failed',
  signup_api_success: 'signup_api_success',
  signup_meta_connect_try_again_click: 'signup_meta_connect_try_again_click',
  // ZC-3324: "Migrating from Another provider? click here" under the Send-OTP
  // CTA — `valid` says whether the form let the hand-off through.
  signup_migration_click: 'signup_migration_click',
  // ZC-3324: the close on the Meta card's migration note — back to the phone
  // step, migration flag dropped.
  signup_migration_close_click: 'signup_migration_close_click',
  // S08 migration (Figma 2605-35910): the Step 3 agreement checkbox above
  // the Meta CTA — `checked` is the new state.
  signup_migration_agree_click: 'signup_migration_agree_click',
  signup_onboarding_completed: 'signup_onboarding_completed',
  signup_otp_change_number_click: 'signup_otp_change_number_click',
  signup_otp_confirm_click: 'signup_otp_confirm_click',
  signup_otp_paused: 'signup_otp_paused',
  signup_otp_resend_click: 'signup_otp_resend_click',
  signup_pending_next_click: 'signup_pending_next_click',
  signup_refresh_checked: 'signup_refresh_checked',
  // Fired by OnboardingShell on every pipeline screen/route change — the
  // during-onboarding route tracker (screens map 1:1 to routes + stages).
  signup_screen_view: 'signup_screen_view',
  signup_send_code_click: 'signup_send_code_click',
  signup_support_chat_started: 'signup_support_chat_started',
}

export default trackEvent
